import { AxiosResponse, AxiosError } from 'axios';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import apiService from 'services/api';
import apiRoutes from 'services/api/routes';
import { getAccessToken } from 'lib/auth';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { setSidebarVisibility } from 'store/sidebar/actions';
import moment from 'moment';
import { IStoreState } from 'store';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { IBookmarkObjState } from 'store/bookmark/types';

export const getBookmarks = () => (dispatch: ThunkDispatch<IStoreState, void, AnyAction>) =>
  new Promise<void>(resolve => {
    dispatch(setSidebarVisibility(false));
    dispatch({
      type: 'GET_BOOKMARKS',
    });
    dispatch(setLoading(ELoadingType.SPINNER));
    apiService
      .get(`${apiRoutes.bookmark}s`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then((response: AxiosResponse) => {
        if (response?.data) {
          response.data.sort((a: IBookmarkObjState, b: IBookmarkObjState) => {
            return moment(b.createdAt).diff(a.createdAt);
          });
        }
        dispatch({
          type: 'GET_BOOKMARKS_SUCCESS',
          bookmarks: response.data,
        });
        resolve();
      })
      .catch((error: AxiosError) => {
        const errorMessage = getAxiosErrorMessage(error);
        if (!error.message.includes('401')) {
          handleError(errorMessage);
        }
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  });

export const setBookmarkName = (bookmarkName: string, bookmarkLink: string, bookmarkModule: string) => ({
  type: 'SET_BOOKMARK_NAME',
  bookmarkName,
  bookmarkLink,
  bookmarkModule,
});

export const addBookmark = () => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));
    const { bookmark } = getState();
    const postBody = {
      link: bookmark.bookmarkLink,
      name: bookmark.bookmarkName,
      module: bookmark.bookmarkModule,
    };
    apiService
      .post(apiRoutes.bookmark, postBody, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(() => {
        dispatch({
          type: 'ADD_BOOKMARK_SUCCESS',
        });
        dispatch(getBookmarks());
        dispatch(setBookmarkName('', '', ''));
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const deleteBookmark = (id: string) => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>) => {
    dispatch(setLoading(ELoadingType.SPINNER));
    apiService
      .delete(`${apiRoutes.bookmark}/${id}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(() => {
        dispatch(getBookmarks());
        dispatch(setBookmarkName('', '', ''));
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const renameBookmark = (bookmarkObj: { id?: number; name: string; link: string; module: string }) => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));
    const { bookmark } = getState();
    const postBody = {
      link: bookmarkObj.link,
      name: bookmark.bookmarkName,
      module: bookmarkObj.module,
    };
    apiService
      .post(`${apiRoutes.bookmark}/${bookmarkObj.id}`, postBody, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
      .then(() => {
        dispatch({
          type: 'RENAME_BOOKMARK_SUCCESS',
        });
        dispatch(getBookmarks());
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const setUserSearchInput = (searchInputValue: string) => ({
  type: 'SET_USER_SEARCH_INPUT',
  searchInputValue,
});
